import { Incentive, useCurrenciesMap } from '@app/api';

import {
  convertToCurrency,
  getCurrencyAmountAsFloat,
  getFloatCurrencyAmountAsInteger,
} from 'lib/conversions';
import isNumber from 'lodash/isNumber';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';

export const useIncentiveEditionControllingState = (
  initialState?: Incentive,
) => {
  const [currentIncentive, setIncentive] = useState<undefined | Incentive>(
    undefined,
  );

  const currencyMap = useCurrenciesMap();
  const incentive = useMemo(() => {
    if (currentIncentive) {
      return currentIncentive;
    }

    if (initialState) {
      return initialState;
    }

    return {
      currencyCode: 'EUR',
      amount: 0,
    };
  }, [, currentIncentive, initialState]);

  const currencyInfo = currencyMap[incentive.currencyCode];
  const decimals =
    currencyInfo?.decimal === undefined ? 2 : currencyInfo.decimal;

  const onAmountChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const amount = Number(e.target.value);
      if (isNaN(amount)) {
        return;
      }
      setIncentive({
        currencyCode: incentive.currencyCode,
        amount: getFloatCurrencyAmountAsInteger(amount, decimals),
      });
    },
    [setIncentive, decimals, incentive],
  );

  const onCurrencyChange = useCallback(
    (option: { value: string | number } | null) => {
      if (!option || isNumber(option.value)) {
        return;
      }
      const newCurrencyInfo = currencyMap[option.value];
      if (!currencyInfo || !newCurrencyInfo) {
        return;
      }
      const newAmount = convertToCurrency(
        incentive.amount,
        currencyInfo,
        newCurrencyInfo,
      );
      if (newAmount === undefined) {
        return setIncentive(undefined);
      }
      setIncentive({
        amount: newAmount,
        currencyCode: option.value,
      });
    },
    [setIncentive, currencyMap, currencyInfo, incentive],
  );

  return {
    incentive,
    onCurrencyChange,
    onAmountChange,
    setIncentive,
    floatAmount: getCurrencyAmountAsFloat(incentive.amount, decimals),
  };
};
